import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import styles from "./UpdateCampaignsScreen.module.scss";
import classNames from "classnames";
import updateCampaignsService from "../../../apis/services/admin/updateCampaignsService";
import appVersionsService from "../../../apis/services/admin/appVersionsService";
import { toast } from "react-toastify";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

// Campaign status configuration
const CAMPAIGN_STATUS_CONFIG = {
  PENDING: { label: "Na čekanju", color: "#9CA3AF", bgColor: "#F3F4F6" },
  IN_PROGRESS: { label: "U toku", color: "#3B82F6", bgColor: "#DBEAFE" },
  COMPLETED: { label: "Završeno", color: "#22C55E", bgColor: "#DCFCE7" },
  CANCELLED: { label: "Otkazano", color: "#EF4444", bgColor: "#FEE2E2" },
};

// Device status configuration
const DEVICE_STATUS_CONFIG = {
  PENDING: { label: "Na čekanju", color: "#9CA3AF" },
  COMMAND_SENT: { label: "Komanda poslata", color: "#3B82F6" },
  UPDATED: { label: "Ažurirano", color: "#22C55E" },
  FAILED: { label: "Neuspešno", color: "#EF4444" },
  SKIPPED: { label: "Preskočeno", color: "#F59E0B" },
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return moment(dateString).format("DD.MM.YYYY HH:mm");
};

const UpdateCampaignsScreen = () => {
  const history = useHistory();
  const selectedFiscalDevices = useSelector(
    (state) => state.fiscalDevices.selectedFiscalDevices.data
  );
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  // Create modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [appVersions, setAppVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Form states
  const [formName, setFormName] = useState("");
  const [formTargetVersion, setFormTargetVersion] = useState("");
  const [formOtaUrl, setFormOtaUrl] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formUrlSource, setFormUrlSource] = useState("version");
  const [formSelectedVersionId, setFormSelectedVersionId] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { perPage: 50 };
      if (statusFilter) params.status = statusFilter;
      
      const response = await updateCampaignsService.fetchCampaigns(params);
      setCampaigns(response?.data || response || []);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError("Greška pri učitavanju kampanja");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch app versions for create modal
  const fetchVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const response = await appVersionsService.fetchVersions(false);
      const versions = Array.isArray(response) ? response : response?.data || [];
      setAppVersions(versions);
    } catch (err) {
      console.error("Error fetching app versions:", err);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Open create modal
  const handleCreateClick = () => {
    setFormName("");
    setFormTargetVersion("");
    setFormOtaUrl("");
    setFormNotes("");
    setFormUrlSource("version");
    setFormSelectedVersionId("");
    fetchVersions();
    setCreateModalOpen(true);
  };

  // Handle version selection
  const handleVersionChange = (e) => {
    const versionId = e.target.value;
    setFormSelectedVersionId(versionId);
    if (versionId) {
      const version = appVersions.find(v => v.id.toString() === versionId);
      if (version) {
        setFormTargetVersion(version.version);
        setFormOtaUrl(version.otaUrl);
      }
    } else {
      setFormTargetVersion("");
      setFormOtaUrl("");
    }
  };

  // Handle URL source change
  const handleUrlSourceChange = (source) => {
    setFormUrlSource(source);
    if (source === "version") {
      setFormTargetVersion("");
      setFormOtaUrl("");
      setFormSelectedVersionId("");
    }
  };

  // Submit create form
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error("Naziv kampanje je obavezan");
      return;
    }
    if (!formTargetVersion.trim()) {
      toast.error("Ciljna verzija je obavezna");
      return;
    }
    if (!formOtaUrl.trim()) {
      toast.error("OTA URL je obavezan");
      return;
    }
    if (selectedFiscalDevices.length === 0) {
      toast.error("Morate izabrati uređaje na stranici Fiskalni uređaji");
      return;
    }

    setFormSubmitting(true);
    try {
      const data = {
        name: formName.trim(),
        targetVersion: formTargetVersion.trim(),
        otaUrl: formOtaUrl.trim(),
        notes: formNotes.trim() || null,
        fiscalDeviceIds: selectedFiscalDevices.map(d => d.id),
      };

      const result = await updateCampaignsService.createCampaign(data);
      toast.success("Kampanja uspešno kreirana");
      setCreateModalOpen(false);
      fetchCampaigns();
      
      // Navigate to campaign detail
      if (result?.id) {
        history.push(`/update-campaigns/${result.id}`);
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
      toast.error("Greška pri kreiranju kampanje");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Render loading state
  const renderLoading = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorState}>
      <p>⚠️ {error}</p>
      <button className={styles.retryButton} onClick={fetchCampaigns}>
        Pokušaj ponovo
      </button>
    </div>
  );

  // Render campaign card
  const renderCampaignCard = (campaign) => {
    const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.status] || CAMPAIGN_STATUS_CONFIG.PENDING;
    const progress = campaign.totalDevices > 0 
      ? (campaign.updatedDevices / campaign.totalDevices) * 100 
      : 0;

    return (
      <div 
        key={campaign.id} 
        className={styles.campaignCard}
        onClick={() => history.push(`/update-campaigns/${campaign.id}`)}
      >
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <h3>{campaign.name}</h3>
            <span 
              className={styles.statusBadge}
              style={{ 
                backgroundColor: statusConfig.bgColor, 
                color: statusConfig.color 
              }}
            >
              {statusConfig.label}
            </span>
          </div>
          <span className={styles.cardDate}>{formatDate(campaign.createdAt)}</span>
        </div>

        <div className={styles.cardInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Ciljna verzija:</span>
            <span className={styles.infoValue}>{campaign.targetVersion}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Kreirao:</span>
            <span className={styles.infoValue}>
              {campaign.createdBy?.firstName} {campaign.createdBy?.lastName}
            </span>
          </div>
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span>Napredak</span>
            <span>{campaign.updatedDevices} / {campaign.totalDevices}</span>
          </div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {campaign.statusBreakdown && (
          <div className={styles.statusBreakdown}>
            {Object.entries(campaign.statusBreakdown).map(([status, count]) => {
              if (count === 0) return null;
              const config = DEVICE_STATUS_CONFIG[status];
              return (
                <span 
                  key={status}
                  className={styles.breakdownChip}
                  style={{ backgroundColor: config?.color }}
                >
                  {config?.label}: {count}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Update Kampanje</h1>
          <div className={styles.headerActions}>
            <select
              className={styles.statusFilter}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Sve kampanje</option>
              {Object.entries(CAMPAIGN_STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <button className={styles.createButton} onClick={handleCreateClick}>
              + Nova kampanja
            </button>
          </div>
        </div>

        {/* Selected devices info */}
        {selectedFiscalDevices.length > 0 && (
          <div className={styles.selectedDevicesInfo}>
            <span>📱 Izabrano uređaja: <strong>{selectedFiscalDevices.length}</strong></span>
            <span className={styles.infoHint}>
              (Izaberite uređaje na stranici "Fiskalni uređaji" pre kreiranja kampanje)
            </span>
          </div>
        )}

        {/* Content */}
        {loading && renderLoading()}
        {error && !loading && renderError()}
        {!loading && !error && campaigns.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🚀</div>
            <p>Nema kampanja ažuriranja</p>
            <button className={styles.createButton} onClick={handleCreateClick}>
              Kreiraj prvu kampanju
            </button>
          </div>
        )}
        {!loading && !error && campaigns.length > 0 && (
          <div className={styles.campaignsList}>
            {campaigns.map(renderCampaignCard)}
          </div>
        )}

        {/* Create Campaign Modal */}
        <BaseModal
          modalOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title="Nova kampanja ažuriranja"
        >
          <form onSubmit={handleCreateSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Naziv kampanje *</label>
              <input
                type="text"
                className={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="npr. Update to 3.0.0 - Batch 1"
              />
            </div>

            {/* URL Source Toggle */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Izvor verzije</label>
              <div className={styles.urlSourceToggle}>
                <button
                  type="button"
                  className={classNames(
                    styles.toggleButton,
                    formUrlSource === "version" && styles.toggleButtonActive
                  )}
                  onClick={() => handleUrlSourceChange("version")}
                >
                  📦 Registrovana verzija
                </button>
                <button
                  type="button"
                  className={classNames(
                    styles.toggleButton,
                    formUrlSource === "custom" && styles.toggleButtonActive
                  )}
                  onClick={() => handleUrlSourceChange("custom")}
                >
                  ✏️ Custom
                </button>
              </div>
            </div>

            {/* Version Selector */}
            {formUrlSource === "version" && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Izaberi verziju *</label>
                {loadingVersions ? (
                  <p className={styles.loadingText}>Učitavanje verzija...</p>
                ) : (
                  <select
                    className={styles.select}
                    value={formSelectedVersionId}
                    onChange={handleVersionChange}
                  >
                    <option value="">-- Izaberi verziju --</option>
                    {appVersions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.version}
                        {version.isLatest ? " ⭐ (Latest)" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Custom Version & URL */}
            {formUrlSource === "custom" && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Ciljna verzija *</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formTargetVersion}
                    onChange={(e) => setFormTargetVersion(e.target.value)}
                    placeholder="3.0.0"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>OTA URL *</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={formOtaUrl}
                    onChange={(e) => setFormOtaUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </>
            )}

            {/* Show selected version info */}
            {formUrlSource === "version" && formTargetVersion && (
              <div className={styles.selectedVersionInfo}>
                <div><strong>Verzija:</strong> {formTargetVersion}</div>
                <div><strong>URL:</strong> <a href={formOtaUrl} target="_blank" rel="noopener noreferrer">{formOtaUrl}</a></div>
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.label}>Napomene</label>
              <textarea
                className={styles.textarea}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Opcione napomene o kampanju..."
                rows={3}
              />
            </div>

            {/* Selected devices count */}
            <div className={styles.devicesAlert}>
              {selectedFiscalDevices.length > 0 ? (
                <span className={styles.devicesAlertSuccess}>
                  ✓ Izabrano uređaja: {selectedFiscalDevices.length}
                </span>
              ) : (
                <span className={styles.devicesAlertWarning}>
                  ⚠️ Nema izabranih uređaja. Izaberite uređaje na stranici "Fiskalni uređaji".
                </span>
              )}
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setCreateModalOpen(false)}
                disabled={formSubmitting}
              >
                Otkaži
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={formSubmitting || !formName.trim() || !formTargetVersion || !formOtaUrl || selectedFiscalDevices.length === 0}
              >
                {formSubmitting ? "Kreiranje..." : "Kreiraj kampanju"}
              </button>
            </div>
          </form>
        </BaseModal>
      </div>
    </DashboardLayout>
  );
};

export default UpdateCampaignsScreen;

